<template>
  <NuxtLayout>
    <NuxtPage />
    <AsyncCookieManagement v-if="!ipBlock" />
  </NuxtLayout>
</template>
<script lang="ts" setup>
import {
  computed,
  defineAsyncComponent,
  onBeforeMount,
  onMounted,
  onUnmounted,
  setLoginScene,
  useCookie,
  useHead,
  useI18n,
  useLocaleHead,
  useRequestHeaders,
  useRoute,
  useShareId,
  watch,
} from '#imports'
import '@/assets/styles/reset.less'
import { useLayout } from '@/store/layout'
import { StorageKey } from '@/types/enum'
import initialTrack from '@/utils/initialTrack'
import type { Meta } from '@unhead/schema'
import { useEventListener } from '@vueuse/core'
import uaParser from 'ua-parser-js'
import { hydrateOnVisible } from 'vue'
import DisplayMode from '~/composables/DisplayMode'
import { useUserInfo } from '~/store/userInfo'
import $track from '~/track/webYunpanPublicModule'
import { synchronizeClientLanguage } from '~/utils'
import initVconsole from '~/utils/initVconsole'
import { localeReg } from '~/utils/removeLocaleFromPath'
import webDeepLinkInterceptor from '~/utils/webDeepLinkInterceptor'

const route = useRoute()
const layoutStore = useLayout()
const head = useLocaleHead({
  addDirAttribute: true,
  identifierAttribute: 'id',
  addSeoAttributes: true,
})
const { t, locale } = useI18n()
const serverRenderTime = computed(() => (import.meta.server ? new Date().toUTCString() : undefined))
const ipBlock = computed(() => !!useUserInfo().ipBlock)
const AsyncCookieManagement = defineAsyncComponent({
  loader: () => import('~/layouts/common/CookieManagementWrapper.vue'),
  hydrate: hydrateOnVisible(),
})

useHead({
  htmlAttrs: {
    dir: head.value.htmlAttrs?.dir,
    lang: head.value.htmlAttrs?.lang,
  },
  link: head.value.link.map(link => ({
    id: link.id,
    href: link.hreflang === 'x-default' ? link.href.replace(localeReg, '') : link.href,
    hrefLang: link.hrefLang,
    rel: link.rel,
  })),
  meta() {
    const origin: Meta[] = [
      {
        name: 'keywords',
        content: t('page-keyword'),
      },
      ...head.value.meta.map(meta => ({
        content: meta.content,
        property: meta.property,
      })),
    ]
    if (serverRenderTime.value) {
      origin.push({
        content: serverRenderTime.value,
        name: 'server-render-time',
      })
    }
    return origin
  },
})

const setTheme = () => {
  layoutStore.isMobile = window.matchMedia('only screen and (max-width: 768px)').matches
}

/* 根据 user-agent header 判断是否为手机，对布局有影响 */
;(() => {
  if (import.meta.client) return
  const deviceType = uaParser(useRequestHeaders(['user-agent'])['user-agent']).device.type

  if (!deviceType) return

  layoutStore.isMobile = deviceType === 'mobile'
})()
// endregion

useEventListener('resize', setTheme)
useEventListener('click', webDeepLinkInterceptor)

onBeforeMount(setTheme)
onBeforeMount(initialTrack)
onBeforeMount(DisplayMode.initDisplayModeInCookie)
onBeforeMount(synchronizeClientLanguage) // 同步客户端语言
onMounted(() => {
  if (useCookie(StorageKey.ACCESS_TO_VISIT).value) return
  const { origin, pathname } = window.location
  const shareId = useShareId()
  $track.web_yunpan_block_page_show({
    current_address: origin + pathname,
    page_path: pathname.replace(`/${locale.value}`, '').replace(/^(\/\w+)\S+$/, '$1'),
    share_id: shareId.value,
  })
})

// fix: 路由更新后会创建新的 ppa 实例, 公参丢失, 需要在创建实例后重新补上公参
watch(
  () => route.fullPath,
  () => setTimeout(initialTrack, 0),
)
watch(() => route.path, setLoginScene, { immediate: true })

/**
 * 这部分逻辑是为了修复 el-dialog 的缺陷
 * 当页面有滚动条时，打开弹窗，隐藏滚动条，同时为了补偿隐藏滚动条而增加的宽度，会减小元素宽度 17px
 * 问题在于，Chrome 107.0.5304.107（正式版本） （64 位）是滚动条是在 html 元素，而 el-dialog 操作的是 body 元素
 * 结果就是不仅滚动条没有被隐藏，页面宽度反而减小了 17px
 */
let observer: MutationObserver

onMounted(() => {
  const pcHeaderElt = document.getElementById('pc_header')
  if (!pcHeaderElt) return
  // 创建一个观察器实例并传入回调函数
  observer = new MutationObserver(mutations => {
    const targetClassName = 'el-popup-parent--hidden'
    const widthReg = /calc\(100% - \d+px\)/i
    const [body] = mutations
    const target = body.target as HTMLBodyElement
    const htmlElt = document.documentElement
    if (!body.oldValue?.includes(targetClassName) && target.className.includes(targetClassName)) {
      // dialog 显示
      htmlElt.style.overflow = 'hidden'
      if (widthReg.test(target.style.width)) pcHeaderElt.style.width = target.style.width
    } else {
      // dialog 隐藏
      htmlElt.style.overflow = 'auto'
      if (widthReg.test(pcHeaderElt.style.width)) {
        const attr = pcHeaderElt.getAttribute('style')?.replace(/\s?width:\s?calc\(100% - \d+px\);?/, '')
        attr && pcHeaderElt.setAttribute('style', attr)
      }
    }
  })
  observer.observe(document.body, {
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ['class'],
  })
})

onMounted(() => {
  initVconsole()
  window.Adjust?.initSdk?.({ appToken: 'fxlsckqfvitc', environment: process.dev ? 'sandbox' : 'production' })
})

onUnmounted(() => {
  observer?.disconnect?.()
})
</script>
